import * as React from 'react';

/**
 * Shared mobile-viewport detector for JS-side branches (e.g. defaulting
 * a layout to grid on small screens, switching between a table and a
 * card list, etc.). For pure CSS layout decisions, prefer
 * `@media (max-width: 768px)` directly — this hook only exists for
 * places that need a JS boolean.
 *
 * The 768px threshold mirrors the existing breakpoint used in the
 * AppShell (see `src/components/AppSidebar/useSidebarCollapse.ts` and
 * `src/components/AppSidebar/AppSidebar.module.scss` line 176) and the
 * `--bp-md` CSS variable declared in `src/assets/styles/variables.scss`.
 * Update both if the threshold ever changes.
 */
export const MOBILE_BREAKPOINT_PX = 768;
const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`;

const getInitialValue = (query: string): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(query).matches;
};

/**
 * Returns `true` whenever the viewport is below the mobile breakpoint.
 * Updates on resize via `matchMedia` so consumers re-render when the
 * user rotates or resizes the window.
 *
 * Pass a custom query string to override the default (e.g.
 * `useIsMobile('(max-width: 479px)')` for a sub-phone surface).
 */
export function useIsMobile(query: string = MOBILE_QUERY): boolean {
  const [matches, setMatches] = React.useState<boolean>(() => getInitialValue(query));

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

    // Safari < 14 doesn't support `addEventListener` on MediaQueryList.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      setMatches(mq.matches);
      return () => mq.removeEventListener('change', handler);
    }
    mq.addListener(handler);
    setMatches(mq.matches);
    return () => mq.removeListener(handler);
  }, [query]);

  return matches;
}

export default useIsMobile;
